import { mix } from 'polished';

import { useTheme } from '@pega/cosmos-react-core';

type Theme = ReturnType<typeof useTheme>;

export const getPlaceholderStyles = (theme: Theme): string => `
  .ProseMirror p.is-editor-empty:first-child::before {
    color: ${mix(
      theme.base.transparency['transparent-3'],
      theme.base.palette['foreground-color'],
      theme.components['form-control']['background-color']
    )};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
`;

export const getEditorStyles = (theme: Theme): string => `
  html {
    overflow: hidden;
  }

  body {
    min-height: 3rem;
    padding: ${theme.base.spacing};
    background: unset;
    cursor: text;
  }

  #tiptap-root {
    min-height: 100%;
  }

  .ProseMirror {
    outline: none;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    min-height: 100%;
    cursor: text;
  }
`;

export const getImageStyles = (theme: Theme): string => `
  .ProseMirror img {
    max-width: 100%;
    height: auto;
    cursor: pointer;
  }

  .ProseMirror img.ProseMirror-selectednode {
    outline: calc(0.125 * ${theme.base.spacing}) solid ${theme.base.palette.interactive};
  }
`;

export const getTableStyles = (theme: Theme): string => `
  .ProseMirror table,
  .ProseMirror table thead,
  .ProseMirror table tbody,
  .ProseMirror table colgroup,
  .ProseMirror table col,
  .ProseMirror table tr,
  .ProseMirror table td,
  .ProseMirror table th {
    border: none;
  }

  .ProseMirror table {
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    border-top: calc(0.0625 * ${theme.base.spacing}) solid ${theme.components['form-control']['border-color']};
    border-left: calc(0.0625 * ${theme.base.spacing}) solid ${theme.components['form-control']['border-color']};
  }

  .ProseMirror table td,
  .ProseMirror table th {
    min-width: 1em;
    border-right: calc(0.0625 * ${theme.base.spacing}) solid ${theme.components['form-control']['border-color']};
    border-bottom: calc(0.0625 * ${theme.base.spacing}) solid ${theme.components['form-control']['border-color']};
    padding: calc(0.5 * ${theme.base.spacing});
    vertical-align: top;
    position: relative;
  }

  .ProseMirror table th {
    font-weight: bold;
    background-color: ${theme.base.palette['primary-background']};
    text-align: left;
  }

  .ProseMirror table .selectedCell:after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: ${theme.base.palette.interactive};
    opacity: 0.15;
    pointer-events: none;
  }

  .ProseMirror table td,
  .ProseMirror table th {
    position: relative;
  }

  .ProseMirror table .selectedCell::selection,
  .ProseMirror table .selectedCell *::selection {
    background: transparent;
  }

  .ProseMirror .column-resize-handle {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: calc(0.25 * ${theme.base.spacing});
    background-color: ${theme.base.palette.interactive};
    pointer-events: none;
  }

  .ProseMirror.resize-cursor {
    cursor: col-resize;
  }

  .ProseMirror .tableWrapper {
    overflow-x: auto;
  }
`;

export const getListStyles = (): string => `
  /* Hide default marker for aligned list items and use inline marker instead */
  .ProseMirror ul li:has(> p[style*="text-align: center"]),
  .ProseMirror ul li:has(> p[style*="text-align: right"]),
  .ProseMirror ol li:has(> p[style*="text-align: center"]),
  .ProseMirror ol li:has(> p[style*="text-align: right"]) {
    list-style: none;
  }

  /* Add bullet marker inline with centered/right-aligned text */
  .ProseMirror ul li:has(> p[style*="text-align: center"]) > p::before,
  .ProseMirror ul li:has(> p[style*="text-align: right"]) > p::before {
    content: "• ";
  }

  /* Add number marker inline with centered/right-aligned text in ordered lists */
  .ProseMirror ol {
    counter-reset: list-counter;
  }

  .ProseMirror ol li {
    counter-increment: list-counter;
  }

  .ProseMirror ol li:has(> p[style*="text-align: center"]) > p::before,
  .ProseMirror ol li:has(> p[style*="text-align: right"]) > p::before {
    content: counter(list-counter) ". ";
  }
`;

export const getIframeContentStyles = (theme: Theme): string => `
  ${getEditorStyles(theme)}
  ${getImageStyles(theme)}
  ${getTableStyles(theme)}
  ${getPlaceholderStyles(theme)}
  ${getListStyles()}
`;
